import Quill from "quill"
import Embed from "quill/blots/embed"
import Inline from "quill/blots/inline"
import { Registry, type RegistryDefinition } from "parchment"
import Delta from "quill-delta"
import { createSlotMarker, isWholeSlotMarker } from "../model/slots"
import { validUrl } from "../markup/shared"
import type { SourceRun } from "./sourceTokens"

export const SLOT_BLOT_NAME = "telegram-slot"
export const LINK_FORMAT_NAME = "telegram-link"
export const SLOT_CLIPBOARD_TYPE = "application/x-telegram-editor-runs+json"
export interface SlotValue {
  slotId: string
  label: string
}

const slots = new WeakMap<Node, SlotValue>()
const links = new WeakMap<Node, string>()

function safeLabel(label: string): string {
  // Never leak a UUID supplied as a derived label, including in accessible text.
  return (
    label.replace(
      /\{\{slot:[^}]*\}\}|[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}/gi,
      "Ссылка",
    ) || "Ссылка"
  )
}

export function readSlotValue(value: unknown): SlotValue | undefined {
  if (!value || typeof value !== "object") return
  const candidate = value as Partial<SlotValue>
  if (
    typeof candidate.slotId !== "string" ||
    typeof candidate.label !== "string"
  )
    return
  try {
    createSlotMarker(candidate.slotId)
  } catch {
    return
  }
  return { slotId: candidate.slotId, label: safeLabel(candidate.label) }
}

class TelegramSlot extends Embed {
  static blotName = SLOT_BLOT_NAME
  static tagName = "SPAN"
  static className = "telegram-slot"
  static create(value: unknown) {
    const slot = readSlotValue(value)
    if (!slot) throw new TypeError("Invalid internal slot embed")
    const node = super.create() as HTMLElement
    slots.set(node, slot)
    node.textContent = slot.label
    node.setAttribute("contenteditable", "false")
    node.setAttribute("role", "button")
    node.setAttribute("tabindex", "0")
    node.setAttribute("aria-label", `Настроить ссылку: ${slot.label}`)
    return node
  }
  static value(node: Node) {
    return slots.get(node)
  }
  clone() {
    return this.scroll.create(SLOT_BLOT_NAME, slots.get(this.domNode))
  }
}

/** The built-in link sanitizer cannot represent marker URLs. Keep their value
 * in memory, not href/title/data attributes, without changing legacy link blots. */
class TelegramLink extends Inline {
  static blotName = LINK_FORMAT_NAME
  static tagName = "A"
  static className = "telegram-link"
  static create(value: unknown) {
    const node = super.create() as HTMLElement
    if (typeof value === "string" && validUrl(value)) {
      links.set(node, value)
      node.setAttribute("href", isWholeSlotMarker(value) ? "#" : value)
      node.setAttribute("rel", "noopener noreferrer")
      node.setAttribute("target", "_blank")
      node.setAttribute("role", "button")
      node.setAttribute("tabindex", "0")
      if (isWholeSlotMarker(value)) node.classList.add("telegram-shared-link")
    }
    return node
  }
  static formats(node: HTMLElement) {
    return links.get(node)
  }
  clone() {
    // Parchment's default cloneNode(false) cannot copy WeakMap metadata.
    return this.scroll.create(LINK_FORMAT_NAME, links.get(this.domNode))
  }
}

/** Per-editor registry: no Quill.register/global mailing format mutations. */
export function createTelegramRegistry(): Registry {
  const registry = new Registry()
  for (const path of [
    "blots/scroll",
    "blots/block",
    "blots/break",
    "blots/container",
    "blots/cursor",
    "blots/inline",
    "blots/text",
    "formats/bold",
    "formats/italic",
    "formats/underline",
    "formats/strike",
    "formats/code",
  ])
    registry.register(Quill.import(path) as RegistryDefinition)
  registry.register(TelegramSlot, TelegramLink)
  return registry
}

/** V2 adds the complete, validated link destination to each run. It is separate
 * from visible text/labels and is never inferred from pasted HTML attributes. */
type ClipboardRun = SourceRun & { link?: string }

function readInternalRuns(raw: string): ClipboardRun[] | undefined {
  try {
    const payload: unknown = JSON.parse(raw)
    if (
      !payload ||
      typeof payload !== "object" ||
      !("version" in payload) ||
      (payload.version !== 1 && payload.version !== 2) ||
      !("runs" in payload) ||
      !Array.isArray(payload.runs)
    )
      return
    const runs: ClipboardRun[] = []
    for (const run of payload.runs) {
      if (!run || typeof run !== "object") return
      let parsed: ClipboardRun
      if (run.kind === "text" && typeof run.text === "string")
        parsed = { kind: "text", text: run.text }
      else if (run.kind === "slot") {
        const slot = readSlotValue(run)
        if (!slot) return
        parsed = { kind: "slot", ...slot }
      } else return
      if (payload.version === 2 && run.link !== undefined) {
        if (!validUrl(run.link)) return
        parsed.link = run.link
      }
      runs.push(parsed)
    }
    return runs
  } catch {
    return
  }
}

function plainHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html")
  doc
    .querySelectorAll("script,style,iframe,object,template")
    .forEach((node) => node.remove())
  doc.querySelectorAll("br").forEach((node) => node.replaceWith("\n"))
  doc
    .querySelectorAll("p,div,li,blockquote,pre")
    .forEach((node) => node.append("\n"))
  return doc.body.textContent ?? ""
}

/** Capture before Quill's HTML matcher pipeline. Only the versioned custom
 * MIME accepts slots/link destinations, after validation. V1 slot payloads
 * remain readable; V2 writes also preserve named links. HTML contributes text only;
 * no pasted classes, styles, URLs, embeds or arbitrary metadata are trusted. */
export function installSafeClipboard(quill: Quill): () => void {
  const paste = (event: ClipboardEvent) => {
    if (!quill.isEnabled() || !event.clipboardData) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const range = quill.getSelection(true)
    if (!range) return
    const runs = readInternalRuns(
      event.clipboardData.getData(SLOT_CLIPBOARD_TYPE),
    )
    const insertion = new Delta()
    if (runs) {
      runs.forEach((run) =>
        insertion.insert(
          run.kind === "text"
            ? run.text
            : { [SLOT_BLOT_NAME]: { slotId: run.slotId, label: run.label } },
          run.link === undefined ? undefined : { [LINK_FORMAT_NAME]: run.link },
        ),
      )
    } else {
      insertion.insert(
        event.clipboardData.getData("text/plain") ||
          plainHtml(event.clipboardData.getData("text/html")),
      )
    }
    const before = quill.getContents()
    const applied = quill.updateContents(
      new Delta().retain(range.index).delete(range.length).concat(insertion),
      "user",
    )
    // updateContents applies Quill's newline normalization, then synchronous
    // listeners may hydrate raw markers into embeds (or reject the paste).
    // Map the replaced range's endpoint through BOTH changes, rather than
    // measuring the raw clipboard text or clamping it to the document end.
    const proposed = before.compose(applied)
    const accepted = quill.getContents()
    const endpoint = applied.transformPosition(range.index + range.length)
    const caret = proposed.diff(accepted).transformPosition(endpoint)
    quill.setSelection(caret, 0, "silent")
  }
  const copy = (event: ClipboardEvent) => {
    if (!event.clipboardData) return
    const range = quill.getSelection()
    if (!range) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const runs: ClipboardRun[] = []
    for (const op of quill.getContents(range.index, range.length).ops) {
      let run: ClipboardRun | undefined
      if (typeof op.insert === "string") run = { kind: "text", text: op.insert }
      else if (op.insert) {
        const slot = readSlotValue(op.insert[SLOT_BLOT_NAME])
        if (slot) run = { kind: "slot", ...slot }
      }
      if (run) {
        const link = op.attributes?.[LINK_FORMAT_NAME]
        if (validUrl(link)) run.link = link
        runs.push(run)
      }
    }
    event.clipboardData.setData(
      "text/plain",
      runs.map((run) => (run.kind === "text" ? run.text : run.label)).join(""),
    )
    event.clipboardData.setData(
      SLOT_CLIPBOARD_TYPE,
      JSON.stringify({ version: 2, runs }),
    )
    if (event.type === "cut" && quill.isEnabled())
      quill.deleteText(range.index, range.length, "user")
  }
  quill.root.addEventListener("paste", paste, true)
  quill.root.addEventListener("copy", copy, true)
  quill.root.addEventListener("cut", copy, true)
  return () => {
    quill.root.removeEventListener("paste", paste, true)
    quill.root.removeEventListener("copy", copy, true)
    quill.root.removeEventListener("cut", copy, true)
  }
}
